
import { Injectable, NgZone } from '@angular/core';
import { Channel, Socket } from 'phoenix';
import { BehaviorSubject, filter, Observable, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GolfTournament, GolfTournamentDelta } from '../_interfaces/golf-tournament-socket';
import { SsrlocalstoreService } from '../_services/ssr-localstore.service';


/**
 * One Socket per team_id 
 */
class SocketConnections {
    private connections: { [team_id: string]: Socket } = {};

    add(team_id: string, connection: Socket): void {
        if (this.connections[team_id]) return;
        this.connections[team_id] = connection;
    }

    get(team_id: string): Socket | null {
        return team_id ? this.connections[team_id] ?? null : null;
    }

    remove(team_id: string): void {
        const sock = this.connections[team_id];
        if (!sock) return;
        try {
            sock.disconnect();
        } finally {
            delete this.connections[team_id];
        }
    }
}

/**
 * One golf_tournament:<team_id> Channel per team_id.
 */
class TournamentChannels {
    private channels: { [team_id: string]: Channel } = {};

    add(team_id: string, channel: Channel): void {
        if (this.channels[team_id]) return;
        this.channels[team_id] = channel;
    }

    get(team_id: string): Channel | null {
        return team_id ? this.channels[team_id] ?? null : null;
    }

    remove(team_id: string): void {
        const ch = this.channels[team_id];
        if (!ch) return;
        try {
            ch.leave();
        } finally {
            delete this.channels[team_id];
        }
    }
}

/**
 * Prevents racing/duplicate connects for the same team_id
 */
class ConnectionStatus {
    public pending = new BehaviorSubject(false);
    private team_id: string | null = null;

    getState(): boolean {
        return this.pending.getValue();
    }

    getTeamID(): string {
        return typeof this.team_id === 'string' ? this.team_id : '';
    }

    setState(state: boolean) {
        this.pending.next(state);
    }

    setTeamID(id: string) {
        this.team_id = id;
    }
}

@Injectable({ providedIn: 'root' })
export class GolfTournamentSocketService {
    // FOR LOCAL TEST ONLY
    private readonly ENABLE_FAKE_GOLF_DELTA: boolean = false;
    private fakeDeltaTimers: { [team_id: string]: ReturnType<typeof setInterval> } = {};
    //
    private socket_connections = new SocketConnections();
    private channel_pool = new TournamentChannels();
    private socket_state = new ConnectionStatus();

    /**
     * Live tournament state for each team_id.
     * Multiple components can subscribe without re-joining channels.
     */
    private tournamentSubjects: {
        [team_id: string]: BehaviorSubject<GolfTournament | null>;
    } = {};

    /**
     * Ensure we never attach duplicate 'golf_delta' listeners,
     * we mark it here per team_id.
     */
    private deltaListenerAttached: { [team_id: string]: boolean } = {};

    private deltaTickSubjects: { [team_id: string]: BehaviorSubject<number> } = {};

    constructor(
      private ssr: SsrlocalstoreService,
      private zone: NgZone,
    ) {}

    public deltaTick$(team_id: string): Observable<number> {
        return this.getOrCreateDeltaTickSubject(team_id).asObservable();
    }

    private getOrCreateDeltaTickSubject(team_id: string): BehaviorSubject<number> {
        if (!this.deltaTickSubjects[team_id]) {
            this.deltaTickSubjects[team_id] = new BehaviorSubject<number>(0);
        }
        return this.deltaTickSubjects[team_id];
    }

    /**
     * Observable with the latest tournament model (initial join payload + patched deltas).
     */
    public tournament$(team_id: string): Observable<GolfTournament | null> {
        return this.getOrCreateTournamentSubject(team_id).asObservable();
    }

    /**
     * Connect to Fan Socket.
     *
     * Similar to what Tennis LiveScoreService does:
     * - connect to `${ELIXIR_SOCKET_HOST}/socket`
     * - use params with a tournament identifier (here: golf_tournament)
     */
    public connect(team_id: string): Promise<boolean> {
      if (!this.ssr.isClientSide) {
        // SSR: do not attempt WebSocket creation
        return Promise.resolve(false);
      }

      try {
        return new Promise((completed) => {
          if (this.socket_connections.get(team_id)) {
            completed(true);
            return;
          }

          if (this.socket_state.getState() && this.socket_state.getTeamID() === team_id) {
            this.socket_state.pending
              .pipe(filter((p) => !p), take(1))
              .subscribe(() => completed(true));
            return;
          }

          this.socket_state.setState(true);
          this.socket_state.setTeamID(team_id);

          const connection = new Socket(`${environment.ELIXIR_SOCKET_HOST}/socket`, {
            params: { golf_tournament: team_id },
            logger: (_kind, _msg, _data) => {
              // console.log(`[phoenix:${kind}] ${msg}`, data);
            },
          });

          connection.onError((err) => {
            console.error('[GolfTournamentSocket] Error connecting', err);
            connection.disconnect();
            this.socket_state.setState(false);
            completed(false);
          });

          connection.onOpen(() => {
            this.socket_connections.add(team_id, connection);
            this.socket_state.setState(false);
            completed(true);
          });

          connection.connect();
        });
      } catch (err) {
        throw err;
      }
    }

    private normalizeTournamentPayload(payload: any): GolfTournament | null {
        const data = payload?.response ?? payload;
        if (Array.isArray(data)) return (data[0] ?? null) as GolfTournament; // your case
        return (data ?? null) as GolfTournament;
    }

    /**
     * Join golf tournament channel:
     *
     * golf_tournament: (id) where the ID is the team Id you get from the Golf Schedule Entry
     *
     * when you join the channel it will respond back with the same data as the API /golf/tournaments/%@
     * => resolve('ok') response as the full tournament and push it to the subject.
     */
    public async joinTournamentChannel(team_id: string): Promise<GolfTournament> {
        if (!this.ssr.isClientSide) throw new Error('Socket join attempted during SSR (blocked)');
        const ok = await this.connect(team_id);
        if (!ok) throw new Error('Unable to connect to golf tournament socket');

        const existing = this.channel_pool.get(team_id);
        if (existing) {
            const current = this.getOrCreateTournamentSubject(team_id).getValue();
            if (current) return current;

            const refreshedPayload = await this.pushCurrent(existing);
            const refreshed = this.normalizeTournamentPayload(refreshedPayload);
            if (refreshed) {
                this.getOrCreateTournamentSubject(team_id).next(refreshed);
                return refreshed;
            }
            return {} as GolfTournament;
        }

        const socket = this.socket_connections.get(team_id) as Socket;
        const channel = socket.channel(`golf_tournament:${team_id}`);

        return await new Promise((completed, rejected) => {
            channel.join()
                .receive('ok', (payload: any) => {
                    const tournament = this.normalizeTournamentPayload(payload);
                    if (!tournament) return rejected(new Error('Join returned empty payload'));

                    this.channel_pool.add(team_id, channel);
                    this.getOrCreateTournamentSubject(team_id).next(tournament);

                    this.attachDeltaListener(team_id, channel);

                    // start fake deltas for local testing
                    // this.startFakeDeltaLoop(team_id);

                    completed(tournament);
                })
                .receive('error', (response: any) => rejected(new Error('Joining golf tournament channel failed')));
        });
    }


    /**
     * Disconnect / cleanup for a team tournament stream.
     */
    public disconnect(team_id: string): void {
        this.channel_pool.remove(team_id);
        this.socket_connections.remove(team_id);

        this.deltaListenerAttached[team_id] = false;

        this.deltaTickSubjects[team_id]?.next(0);

        const subj = this.tournamentSubjects[team_id];
        subj?.next(null);

        const t = this.fakeDeltaTimers?.[team_id];
        if (t) {
          clearInterval(t);
          delete this.fakeDeltaTimers?.[team_id];
        }
    }

    private getOrCreateTournamentSubject(team_id: string): BehaviorSubject<GolfTournament | null> {
        if (!this.tournamentSubjects[team_id]) {
            this.tournamentSubjects[team_id] = new BehaviorSubject<GolfTournament | null>(null);
        }
        return this.tournamentSubjects[team_id];
    }

    /**
     * - NOT replace whole tournament
     * - Merge patch into current tournament
     */
    private attachDeltaListener(team_id: string, channel: Channel): void {
      if (this.deltaListenerAttached[team_id]) return;
      this.deltaListenerAttached[team_id] = true;

      channel.on('golf_delta', (payload) => {
        this.zone.run(() => {
          const delta = this.normalizeTournamentPayload(payload);
          if (!delta) return;

          const tickSubj = this.getOrCreateDeltaTickSubject(team_id);
          const nextTick = tickSubj.getValue() + 1;

          tickSubj.next(nextTick);

          const subj = this.getOrCreateTournamentSubject(team_id);
          const current = subj.getValue();

          let patched: GolfTournament;
          if (!current) {
            patched = this.deepMergeById({} as GolfTournament, delta) as GolfTournament;
          } else {
            patched = this.deepMergeById(current, delta) as GolfTournament;
          }

          subj.next(patched);

        //   console.log('[golf_delta processed]', {
        //     team_id,
        //     tick: nextTick,
        //     deltaKeys: Object.keys(delta || {}),
        //   });
        });
      });
    }

    private pushCurrent(channel: Channel): Promise<any> {
        return new Promise((completed) => {
            channel.push('current', {})
                .receive('ok', (payload: any) => completed(payload))
                .receive('error', () => completed(null));
        });
    }

    /**
     * ID-aware deep merge for partial deltas.
     * - objects merge recursively
     * - arrays of objects with "id" merge by id (patch existing items)
     * - primitives overwrite
     */
    private deepMergeById(target: any, patch: any): any {
        if (patch === null || patch === undefined) return target;

        if (!this.isObject(target) || !this.isObject(patch)) {
            return patch;
        }

        if (Array.isArray(target) && Array.isArray(patch)) {
            return this.mergeArray(target, patch);
        }

        const out: any = { ...target };

        Object.keys(patch).forEach((key) => {
            const pVal = patch[key];
            const tVal = target[key];

            if (Array.isArray(tVal) && Array.isArray(pVal)) {
                out[key] = this.mergeArray(tVal, pVal);
                return;
            }

            if (this.isObject(tVal) && this.isObject(pVal)) {
                out[key] = this.deepMergeById(tVal, pVal);
                return;
            }

            out[key] = pVal;
        });

        return out;
    }

    private mergeArray(targetArr: any[], patchArr: any[]): any[] {
        const hasId = (x: any) =>
            this.isObject(x) && (typeof x.id === 'string' || typeof x.id === 'number');

        const isIdObjectArray =
            patchArr.every(hasId) &&
            targetArr.every(hasId);

        if (isIdObjectArray) return this.mergeArrayById(targetArr, patchArr);

        // Primitive arrays (numbers/null/strings): merge by index
        const out = targetArr.length ? targetArr.slice() : new Array(patchArr.length);
        for (let i = 0; i < patchArr.length; i++) {
            if (patchArr[i] !== undefined) out[i] = patchArr[i];
        }
        return out;
    }

    private mergeArrayById(targetArr: any[], patchArr: any[]): any[] {
        const hasId = (x: any) =>
            this.isObject(x) && (typeof x.id === 'string' || typeof x.id === 'number');

        const idObjects =
            patchArr.every(hasId) &&
            targetArr.every(hasId);

        if (!idObjects) return patchArr;

        const out = [...targetArr];
        const indexById = new Map<string | number, number>();
        out.forEach((item, idx) => indexById.set(item.id, idx));

        patchArr.forEach((patchItem) => {
            const idx = indexById.get(patchItem.id);
            if (idx === undefined) {
                out.push(patchItem);
                indexById.set(patchItem.id, out.length - 1);
            } else {
                out[idx] = this.deepMergeById(out[idx], patchItem);
            }
        });

        return out;
    }

    private isObject(value: any): boolean {
        return value !== null && typeof value === 'object';
    }

    private startFakeDeltaLoop(team_id: string): void {
      if (!this.ENABLE_FAKE_GOLF_DELTA) return;
      if (!this.ssr.isClientSide) return;
      if (this.fakeDeltaTimers[team_id]) return;

      this.fakeDeltaTimers[team_id] = setInterval(() => {
        const subj = this.getOrCreateTournamentSubject(team_id);
        const current = subj.getValue();
        if (!current?.events?.length) return;

        // Pick the stroke-play event (same as your component)
        const eventIdx = current.events.findIndex((e: any) => e.type === 'stroke_play');
        if (eventIdx < 0) return;

        const scores = (current.events[eventIdx] as any).scores as any[];
        if (!Array.isArray(scores) || scores.length === 0) return;

        // Choose a player to mutate: prefer one with round_1
        const scoreIdx = scores.findIndex((s) => !!s?.round_1) >= 0
          ? scores.findIndex((s) => !!s?.round_1)
          : 0;

        const target = scores[scoreIdx];
        if (!target) return;

        const playerId = target.player?.id ?? target.player_id ?? target.id; // fallback
        const round1 = target.round_1 ?? {};

        // Simple deterministic “change”: increment strokes + advance thru
        const prevStrokes = typeof round1.strokes === 'number' ? round1.strokes : 0;
        const prevThruRaw = (round1.thru ?? '0').toString();
        const prevThruNum = parseInt(prevThruRaw.replace('*', ''), 10);
        const nextThruNum = Number.isFinite(prevThruNum) ? Math.min(prevThruNum + 1, 18) : 1;

        const nextStrokes = prevStrokes + 1;
        const nextThru = prevThruRaw.includes('*') ? `${nextThruNum}*` : `${nextThruNum}`;

        // Update stats a bit so signatures change reliably
        const prevStats = (round1.stats && typeof round1.stats === 'object') ? round1.stats : {};
        const nextStats = {
          ...prevStats,
          par: typeof prevStats.par === 'number' ? prevStats.par + 1 : 1,
        };

        // Build a minimal delta patch 
        const deltaPatch: any = {
          id: current.id,
          events: [
            {
              id: (current.events[eventIdx] as any).id,
              scores: [
                {
                  id: target.id,
                  round_1: {
                    ...round1,
                    strokes: nextStrokes,
                    thru: nextThru,
                    stats: nextStats,
                  },
                  // Keep total stats aligned too
                  stats: {
                    ...(target.stats ?? {}),
                    par: typeof (target.stats?.par) === 'number' ? target.stats.par + 1 : 1,
                  },
                  strokes: typeof target.strokes === 'number' ? target.strokes + 1 : (target.strokes ?? 1),
                },
              ],
            },
          ],
        };

        this.applyDelta(team_id, deltaPatch);

        console.log('[FAKE golf_delta]', { team_id, playerId, nextThru, nextStrokes });
      }, 5000);
    }

    private applyDelta(team_id: string, delta: any): void {
      // tick FIRST so UI knows this is delta-driven
      const tickSubj = this.getOrCreateDeltaTickSubject(team_id);
      tickSubj.next(tickSubj.getValue() + 1);

      const subj = this.getOrCreateTournamentSubject(team_id);
      const current = subj.getValue();

      const patched = current
        ? (this.deepMergeById(current, delta) as GolfTournament)
        : (this.deepMergeById({} as GolfTournament, delta) as GolfTournament);

      subj.next(patched);
    }

}
